import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Award, FileText, ChevronRight, Fingerprint } from "lucide-react";
import LSSDLogo from "../components/LSSDLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OperatorProfilePublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/public/operatore/${slug}`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else console.error(err);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1F18]" data-testid="operator-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sheriff-gold" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A1F18] text-center px-4" data-testid="operator-not-found">
        <FileText className="h-16 w-16 text-sheriff-gold/40 mb-4" />
        <h1 className="text-2xl font-heading font-bold text-[#F5E6B8]">Operatore non trovato</h1>
        <p className="text-[#8B7B49] mt-2">Il fascicolo &ldquo;{slug}&rdquo; non esiste o non è accessibile.</p>
        <Link to="/" className="mt-6 text-sheriff-gold hover:underline text-sm uppercase tracking-wider">← Torna alla home</Link>
      </div>
    );
  }

  const enrolled = data.enrolled_at ? new Date(data.enrolled_at) : null;
  const matricola = data.badge_number || `#LSSD-????`;
  const avatarUrl = data.discord_id
    ? `${API}/discord/user/${data.discord_id}/avatar`
    : null;

  return (
    <div className="min-h-screen bg-[#0A1F18] text-[#F5E6B8] relative overflow-hidden" data-testid="operator-profile-page">
      {/* Film grain */}
      <div className="film-grain absolute inset-0 pointer-events-none opacity-40" />

      {/* Watermark diagonale */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="text-sheriff-gold/[0.025] font-heading font-black text-[220px] rotate-[-18deg] tracking-tighter select-none whitespace-nowrap">
          LSSD OFFICIAL
        </div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14 space-y-10">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sheriff-gold/80 hover:text-sheriff-gold text-xs uppercase-label tracking-wider">
            <LSSDLogo className="h-8 w-8" />
            <span>LSSD · Portale Operatori</span>
          </Link>
          <div className="dossier-mono text-[10px] text-sheriff-gold/60 uppercase tracking-[0.3em] hidden sm:block">
            § Dossier Pubblico
          </div>
        </div>

        {/* HEADER — Scheda personale */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-10 items-start">
          <div className="relative">
            <div className="aspect-square rounded-2xl border border-sheriff-gold/40 bg-[#143830] overflow-hidden flex items-center justify-center relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={data.rp_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="operator-avatar"
                />
              ) : (
                <LSSDLogo className="w-32 h-32 opacity-40" />
              )}
              <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-sheriff-gold/20 rounded-2xl" />
            </div>
            <div className="mt-3 text-center">
              <div className="dossier-mono text-[9px] text-sheriff-gold/70 uppercase tracking-[0.25em]">Matricola</div>
              <div className="font-mono text-lg text-sheriff-gold font-bold mt-1" data-testid="operator-badge-number">{matricola}</div>
            </div>
          </div>

          <div>
            <div className="dossier-mono text-[10px] text-sheriff-gold/60 uppercase tracking-[0.3em]">Nome Operatore</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-[#F5E6B8] tracking-tighter mt-1 leading-none" data-testid="operator-name">
              {data.rp_name}
            </h1>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sheriff-gold/15 border border-sheriff-gold/40">
                <ShieldCheck className="h-4 w-4 text-sheriff-gold" />
                <span className="text-sheriff-gold uppercase-label text-sm font-bold tracking-wider">{data.role || "—"}</span>
              </div>
              {enrolled && (
                <div className="text-xs text-[#D4C480]">
                  <span className="uppercase-label text-[10px] text-sheriff-gold/70">In servizio da</span>{" "}
                  <span className="font-mono">{enrolled.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
                </div>
              )}
            </div>
            {data.badges?.length > 0 && (
              <div className="mt-6">
                <div className="dossier-mono text-[10px] text-sheriff-gold/60 uppercase tracking-[0.3em] mb-3">§ Riconoscimenti</div>
                <div className="flex flex-wrap gap-2">
                  {data.badges.map((b) => (
                    <div
                      key={b.key}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-br from-sheriff-gold/20 to-sheriff-gold/5 border border-sheriff-gold/50"
                      title={b.label}
                      data-testid={`badge-${b.key}`}
                    >
                      <span className="text-xl">{b.emoji}</span>
                      <span className="text-xs font-bold uppercase-label text-sheriff-gold tracking-wider">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* TIMELINE Carriera */}
        <div>
          <div className="dossier-mono text-[10px] text-sheriff-gold/60 uppercase tracking-[0.3em] mb-4">§ Cronologia Carriera</div>
          <TimelineList promotions={data.promotions || []} exams={data.exams || []} />
        </div>

        {/* Footer */}
        <div className="pt-8 border-t border-sheriff-gold/15 flex items-center justify-between text-xs text-[#8B7B49]">
          <div className="uppercase-label tracking-wider">
            <Fingerprint className="inline h-3 w-3 mr-1" />
            Dossier ufficiale · LSSD Personnel Administration
          </div>
          <Link to="/" className="text-sheriff-gold/70 hover:text-sheriff-gold uppercase-label tracking-wider">
            Portale Home <ChevronRight className="inline h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}


function TimelineList({ promotions, exams }) {
  const events = [];
  for (const p of promotions) {
    const isDemote = p.action === "demoted" || p.action === "demote";
    events.push({
      key: `p-${p.at}-${p.to_role || p.from_role}`,
      at: p.at,
      icon: isDemote ? "⬇️" : "🎖️",
      title: isDemote ? "Demozione" : "Promozione",
      color: isDemote ? "rose" : "gold",
      details: `${p.from_role || "—"} → ${p.to_role || "—"}`,
      issuer: p.issuer,
    });
  }
  for (const e of exams) {
    if (e.status === "pending") continue;
    events.push({
      key: `e-${e.at}-${e.tier}`,
      at: e.at,
      icon: e.status === "approved" ? "📚" : "✖️",
      title: e.status === "approved" ? "Esame Superato" : "Esame Non Superato",
      color: e.status === "approved" ? "emerald" : "rose",
      details: `Tier: ${e.tier || "—"}${e.score != null ? ` · Punteggio ${e.score}` : ""}`,
    });
  }
  events.sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  if (events.length === 0) {
    return (
      <div className="text-center text-[#8B7B49] py-10 border border-dashed border-sheriff-gold/20 rounded-2xl">
        <FileText className="h-8 w-8 mx-auto text-sheriff-gold/30 mb-2" />
        <div className="uppercase-label text-xs tracking-wider">Nessun evento registrato</div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-2 bottom-2 w-px bg-sheriff-gold/20" />
      <div className="space-y-4">
        {events.map((ev) => (
          <div key={ev.key} className="relative pl-12" data-testid={`timeline-${ev.key}`}>
            <div className={`absolute left-0 top-1 h-9 w-9 rounded-full flex items-center justify-center border ${
              ev.color === "gold" ? "bg-sheriff-gold/20 border-sheriff-gold/60" :
              ev.color === "emerald" ? "bg-emerald-500/20 border-emerald-400/60" :
              "bg-rose-500/20 border-rose-400/60"
            }`}>
              <span className="text-lg">{ev.icon}</span>
            </div>
            <div className="rounded-xl bg-[#143830] border border-sheriff-gold/20 px-4 py-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="uppercase-label text-[10px] text-sheriff-gold/80 tracking-wider">{ev.title}</div>
                  <div className="text-sm text-[#F5E6B8] font-medium mt-0.5">{ev.details}</div>
                  {ev.issuer && (
                    <div className="text-[11px] text-[#8B7B49] mt-1">Redatto da {ev.issuer}</div>
                  )}
                </div>
                <div className="text-[11px] text-[#8B7B49] shrink-0 font-mono">
                  {ev.at ? new Date(ev.at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
